import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, AlertTriangle, Clock, Calendar, MapPin, Ticket as TicketIcon, Loader2 } from 'lucide-react';
import { BACKEND_URL } from '../lib/api';

/**
 * Public ticket verification page.
 *
 * Reachable at /verify/:code — anyone (customer, door staff, security) who
 * scans a VinePass QR code with their phone camera lands here. No login
 * required. Shows event details + validity badge. Does NOT check-in the
 * ticket; only an organiser using the /scanner page can do that.
 */
const STATUS_META = {
  valid: {
    icon: CheckCircle2,
    label: 'Valid Ticket',
    badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    iconColor: 'text-emerald-600',
    ringColor: 'ring-emerald-100',
  },
  checked_in: {
    icon: AlertTriangle,
    label: 'Already Checked In',
    badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
    iconColor: 'text-amber-600',
    ringColor: 'ring-amber-100',
  },
  unpaid: {
    icon: Clock,
    label: 'Payment Pending',
    badgeClass: 'bg-slate-100 text-slate-700 border-slate-200',
    iconColor: 'text-slate-500',
    ringColor: 'ring-slate-100',
  },
  not_found: {
    icon: XCircle,
    label: 'Ticket Not Found',
    badgeClass: 'bg-rose-100 text-rose-800 border-rose-200',
    iconColor: 'text-rose-600',
    ringColor: 'ring-rose-100',
  },
};

const formatDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-ZA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return d;
  }
};

const VerifyTicketPage = () => {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchVerify = async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/tickets/verify/${encodeURIComponent(code)}`,
        );
        if (active) setResult(res.data);
      } catch (e) {
        if (active) setError('We could not verify this ticket right now. Please try again.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchVerify();
    return () => { active = false; };
  }, [code]);

  if (loading) {
    return (
      <div data-testid="verify-loading" className="min-h-[80vh] flex items-center justify-center bg-stone-50">
        <div className="flex flex-col items-center gap-3 text-[#1F3D2B]">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p className="font-serif text-lg">Verifying ticket…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="verify-error" className="min-h-[80vh] flex items-center justify-center bg-stone-50 px-4">
        <div className="max-w-md w-full bg-white border border-stone-200 rounded-2xl p-8 text-center shadow-sm">
          <XCircle className="w-12 h-12 text-rose-500 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-[#1F3D2B] mb-2">Verification Unavailable</h1>
          <p className="text-stone-600 mb-6">{error}</p>
          <Link
            to="/"
            data-testid="verify-back-home"
            className="inline-block bg-[#1F3D2B] text-white px-6 py-3 rounded-full font-medium hover:bg-[#2a5239] transition-colors"
          >
            Back to VinePass
          </Link>
        </div>
      </div>
    );
  }

  const status = result?.status || 'not_found';
  const meta = STATUS_META[status] || STATUS_META.not_found;
  const Icon = meta.icon;
  const ticket = result?.ticket;

  return (
    <div className="min-h-[80vh] bg-stone-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Branded header */}
        <div className="text-center mb-8">
          <div className="inline-block px-4 py-1 bg-[#1F3D2B]/5 text-[#1F3D2B] text-xs tracking-[0.2em] uppercase rounded-full mb-3">
            VinePass · Ticket Verification
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#1F3D2B]">
            {meta.label}
          </h1>
        </div>

        {/* Status card */}
        <div
          data-testid={`verify-status-${status}`}
          className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden"
        >
          <div className="px-8 pt-8 pb-6 flex flex-col items-center text-center border-b border-stone-100">
            <div className={`w-20 h-20 rounded-full bg-white ring-8 ${meta.ringColor} flex items-center justify-center mb-4`}>
              <Icon className={`w-10 h-10 ${meta.iconColor}`} strokeWidth={1.75} />
            </div>
            <span
              className={`inline-flex items-center px-3 py-1 text-xs font-medium tracking-wide uppercase rounded-full border ${meta.badgeClass}`}
            >
              {meta.label}
            </span>
            <p className="mt-4 text-stone-600 max-w-sm">{result?.message}</p>
          </div>

          {/* Ticket details — only render when ticket data present */}
          {ticket ? (
            <div className="px-8 py-6 space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-1">Event</p>
                <p data-testid="verify-event-title" className="font-serif text-xl text-[#1F3D2B] leading-snug">
                  {ticket.event_title}
                </p>
              </div>

              <div className="flex items-start gap-3 text-stone-700">
                <Calendar className="w-4 h-4 mt-1 text-[#1F3D2B] flex-shrink-0" />
                <div>
                  <p data-testid="verify-event-date" className="text-sm">{formatDate(ticket.event_date)}</p>
                  {ticket.event_time && (
                    <p className="text-sm text-stone-500">{ticket.event_time}</p>
                  )}
                </div>
              </div>

              {ticket.event_location && (
                <div className="flex items-start gap-3 text-stone-700">
                  <MapPin className="w-4 h-4 mt-1 text-[#1F3D2B] flex-shrink-0" />
                  <p data-testid="verify-event-location" className="text-sm">{ticket.event_location}</p>
                </div>
              )}

              <div className="flex items-start gap-3 text-stone-700">
                <TicketIcon className="w-4 h-4 mt-1 text-[#1F3D2B] flex-shrink-0" />
                <div>
                  <p data-testid="verify-ticket-type" className="text-sm">{ticket.ticket_type}</p>
                  <p className="text-xs text-stone-400 font-mono mt-0.5">
                    Code · {ticket.ticket_code}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-8 py-6 text-center text-sm text-stone-500">
              If you believe this is an error, please reply to your confirmation email.
            </div>
          )}

          <div className="px-8 py-5 bg-stone-50/60 border-t border-stone-100 text-center">
            <p className="text-xs text-stone-500 leading-relaxed">
              This page is for verification only. Door staff will scan your ticket separately at check-in.
            </p>
          </div>
        </div>

        {/* Soft-conversion CTA — sits below the verification card, stays
            quiet & premium. Only shown when the ticket is found, so we
            don't push marketing on someone holding a broken/invalid code. */}
        {ticket && (
          <div className="mt-10 pt-8 border-t border-stone-200/70 text-center">
            <p className="text-xs uppercase tracking-[0.18em] text-stone-400 mb-3">
              Discover
            </p>
            <Link
              to="/events"
              data-testid="verify-browse-events"
              className="inline-flex items-center gap-2 font-serif text-lg text-[#1F3D2B] hover:text-[#2a5239] transition-colors group"
            >
              Browse more VinePass experiences
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
          </div>
        )}

        {/* Footer link back home */}
        <div className="text-center mt-8">
          <Link
            to="/"
            data-testid="verify-back-home"
            className="text-sm text-stone-400 hover:text-[#1F3D2B] hover:underline"
          >
            ← Back to VinePass
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyTicketPage;
